import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AuthUser from "../../AuthUser";
import { toast } from '../../components/ui/toast';
import Global from "../../APIConfig";
import { Menu, Search, UserCircle, LogOut, ChevronDown, CornerDownLeft } from "lucide-react";

/**
 * Pram Express vendor portal — top bar.
 * Quick-link search (same 8 links as before) plus an AWB track shortcut;
 * profile menu (My Profile / Update Password / Logout); online chip.
 */
const TITLES = [
    [/^\/Customer-Dash/, 'Dashboard'],
    [/^\/Shipping/, 'Shipping'],
    [/^\/Finance/, 'Finance'],
    [/^\/Profile/, 'Profile'],
    [/^\/Createshipment/, 'New Shipment'],
    [/^\/Shipmentlist\//, 'Shipments'],
    [/^\/ShipmentDetails\//, 'Shipment Details'],
    [/^\/ShipmentTracking\//, 'Track Shipment'],
    [/^\/ShipmentLabel/, 'Shipment Label'],
    [/^\/ShipmentInvoice\//, 'Shipment Invoice'],
    [/^\/ShipmentPrintReceipt\//, 'Payment Receipt'],
    [/^\/ConsignmentNote\//, 'Consignment Note'],
    [/^\/CustomerWallet/, 'My Wallet'],
    [/^\/CustomerCredit/, 'My Credit Score'],
    [/^\/UpdateProfile/, 'Update Profile'],
    [/^\/UpdatePassword/, 'Update Password'],
    [/^\/WarehouseList/, 'Warehouses'],
    [/^\/WarehouseAdd/, 'Add Warehouse'],
    [/^\/WarehouseEdit\//, 'Edit Warehouse'],
    [/^\/Developer/, 'API & Integration'],
];

function Header() {
    let navigate = useNavigate();
    let location = useLocation();
    const { logout } = AuthUser();
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [showSelectBox, setShowSelectBox] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const mobileSearchRef = useRef(null);
    let user = null;
    try { user = JSON.parse(localStorage.getItem('user')); } catch (e) { user = null; }

    useEffect(() => {
        const on = () => setIsOnline(true);
        const off = () => setIsOnline(false);
        window.addEventListener('online', on);
        window.addEventListener('offline', off);
        return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
    }, []);

    const title = (TITLES.find(([re]) => re.test(location.pathname)) || [null, 'Dashboard'])[1];

    useEffect(() => {
        if (mobileSearchOpen && mobileSearchRef.current) {
            mobileSearchRef.current.focus();
        }
    }, [mobileSearchOpen]);

    const userlogout = () => {
        logout();
        navigate('/');
        window.location.reload();
    };

    const linkdata = [
        { name: 'Shipment List', link: '/Shipmentlist/All' },
        { name: 'Undelivered Shipment List', link: '/Shipmentlist/B' },
        { name: 'Delivered Shipment List', link: '/Shipmentlist/D' },
        { name: 'Add Shipment', link: '/Createshipment' },
        { name: 'Customer Wallet', link: '/CustomerWallet' },
        { name: 'Customer Credit', link: '/CustomerCredit' },
        { name: 'Update Profile', link: '/UpdateProfile' },
        { name: 'Update Password', link: '/UpdatePassword' },
    ];

    const handlesearchdata = (val) => {
        setSearchTerm(val);
        if (val.length > 2) {
            const filtered = linkdata.filter(item => item.name.toLowerCase().includes(val.toLowerCase()));
            setFilteredData(filtered);
            setShowSelectBox(true);
        } else {
            setShowSelectBox(false);
        }
    };

    const trackAwb = (val) => {
        const awb = (val || '').trim();
        if (!awb) { toast.warn("Please Enter AWB No."); return; }
        setShowSelectBox(false);
        navigate(`/ShipmentTracking/${awb}`);
    };

    return (
        <header className="px-topbar">
            <button className="px-topbar__burger" aria-label="Open navigation"
                onClick={() => window.dispatchEvent(new CustomEvent('px:toggle-nav'))}>
                <Menu size={22} />
            </button>

            <a href="https://pramexpress.com/" target="_blank" rel="noopener noreferrer"><img className="px-topbar__logo" src="/assets/img/pram_logo.jpg" alt="Pram Express" /></a>

            <h1 className="px-topbar__title">{title}</h1>

            <div className="px-topbar__spacer"></div>

            <button className="px-topbar__search-toggle" aria-label="Search" aria-expanded={mobileSearchOpen}
                onClick={() => setMobileSearchOpen(o => !o)}>
                <Search size={20} />
            </button>

            {mobileSearchOpen && (
                <>
                    <div className="px-topbar__search-backdrop" onClick={() => setMobileSearchOpen(false)}></div>
                    <div className="px-topbar__search-pop" role="dialog" aria-label="Search"
                        onKeyDown={(e) => { if (e.key === 'Escape') setMobileSearchOpen(false); }}>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text px-search-icon" aria-hidden="true"><Search size={16} /></span>
                            <input ref={mobileSearchRef} type="text" className="form-control px-search-input"
                                placeholder="Search pages or AWB" aria-label="Search"
                                value={searchTerm} onChange={(e) => handlesearchdata(e.target.value)} />
                        </div>
                        {showSelectBox && (
                            <ul className="px-search-menu px-search-menu--pop">
                                {filteredData.map((item, index) => (
                                    <li key={index}>
                                        <a href={item.link} onClick={() => { setShowSelectBox(false); setMobileSearchOpen(false); }}>
                                            <Search size={15} /> {item.name}
                                        </a>
                                    </li>
                                ))}
                                <li>
                                    <a href="#" onClick={(e) => { e.preventDefault(); trackAwb(searchTerm); setMobileSearchOpen(false); }}>
                                        <CornerDownLeft size={15} /> Track AWB: {searchTerm}
                                    </a>
                                </li>
                            </ul>
                        )}
                    </div>
                </>
            )}

            <div className={`px-topbar__status${isOnline ? '' : ' px-topbar__status--offline'}`}>
                <span className="px-topbar__status-dot"></span>
                {isOnline ? 'Online' : 'Offline'}
            </div>

            <div className="px-topbar__search">
                <div className="input-group input-group-merge">
                    <span className="input-group-text px-search-icon" aria-hidden="true"><Search size={16} /></span>
                    <input type="text" className="form-control px-search-input"
                        placeholder="Search pages or AWB" aria-label="Search"
                        value={searchTerm} onChange={(e) => handlesearchdata(e.target.value)} />
                </div>
                {showSelectBox && (
                    <ul className="px-search-menu">
                        {filteredData.map((item, index) => (
                            <li key={index}>
                                <a href={item.link} onClick={() => setShowSelectBox(false)}>
                                    <Search size={15} /> {item.name}
                                </a>
                            </li>
                        ))}
                        <li>
                            <a href="#" onClick={(e) => { e.preventDefault(); trackAwb(searchTerm); }}>
                                <CornerDownLeft size={15} /> Track AWB: {searchTerm}
                            </a>
                        </li>
                    </ul>
                )}
            </div>

            <div className="px-topbar__profile">
                <button className="px-topbar__profile-btn" onClick={() => setMenuOpen(o => !o)}
                    aria-haspopup="true" aria-expanded={menuOpen}>
                    <span className="px-topbar__profile-avatar">
                        {user && user.customer_image
                            ? <img src={`${Global.logoUrl}${user.customer_image}`} alt={user.name || 'profile'} />
                            : (user && user.name ? user.name.charAt(0).toUpperCase() : 'P')}
                    </span>
                    <span className="px-topbar__profile-name">{user ? user.name : ''}</span>
                    <ChevronDown size={15} className="px-topbar__chevron" />
                </button>
                {menuOpen && (
                    <div className="px-topbar__menu" role="menu">
                        <a className="px-topbar__menu-item" href="/UpdateProfile" role="menuitem">
                            <UserCircle size={17} /> My Profile
                        </a>
                        <a className="px-topbar__menu-item" href="/UpdatePassword" role="menuitem">
                            <UserCircle size={17} /> Update Password
                        </a>
                        <div className="px-topbar__menu-sep"></div>
                        <button className="px-topbar__menu-item" role="menuitem" onClick={userlogout}>
                            <LogOut size={17} /> Logout
                        </button>
                    </div>
                )}
            </div>
        </header>
    );
}

export default Header;
